// Original educational scenarios; checked 2026-11-05.
export const insuranceLessons = [
  {
    "id": "insurance-gap",
    "moduleId": "insurance",
    "title": "保險先買多少？先把需要保護的事情列出來",
    "subtitle": "責任、資源與保障缺口",
    "minutes": 6,
    "tag": "用保障需求看保險",
    "objectives": [
      "用資金需求盤點而非年收入倍數代替個別分析",
      "避免把同一筆資產或給付重複扣除"
    ],
    "paragraphs": [
      "先寫下某個風險事件會造成哪些資金缺口，例如收入中斷、照顧支出或家人生活需求，再確認既有可用資產與可能給付。單一保額公式只是整理工具，不能取代需求、健康狀態與承保條件評估。",
      "照顧家人的無酬工作也可能在事件後產生替代成本。盤點時分清誰繳保費、誰受保障、誰能取得給付，以及資金是否真的可供這個用途；不是把所有帳面資產都當成立即可用現金。",
      "本例的需求金額互不重複、資源可供同一事件使用，忽略通膨、稅及時間價值。結果不代表必須投保該額度，還要確認預算、條款與其他支援。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假設一次清償債務需200萬元、五年生活費共180萬元、另留教育費50萬元，三者不重複，總需求430萬元。",
        "可用資產120萬元，既有同事件適用的保障150萬元，均可供這個用途且沒有重複計算。",
        "簡化缺口430−120−150＝160萬元；重新確認假設，比直接把160萬元當成指定商品保額更重要。"
      ],
      "conclusion": "保障需求從事件與責任出發，保額是盤點結果之一。"
    },
    "pitfalls": [
      "以保費高低取代保障是否符合需求。",
      "把同一筆資金同時列為緊急金、教育費和可用抵減資產。"
    ],
    "glossary": [
      {
        "term": "保障缺口",
        "definition": "特定風險事件的資金需求，扣掉可用資源與適用保障後的尚未覆蓋部分。"
      },
      {
        "term": "保險金額",
        "definition": "契約約定的保障數字，實際給付仍須依事件與條款認定。"
      }
    ],
    "takeaway": "保障需求從事件與責任出發，保額是盤點結果之一。",
    "exercise": "用虛構家庭列出兩種風險事件及需求，不填真實健康資料，也不挑選商品。",
    "quiz": {
      "question": "某事件總需求300萬元，可用資源80萬元，同事件適用保障120萬元；互不重複時簡化缺口是多少？",
      "options": [
        "100萬元",
        "180萬元",
        "300萬元"
      ],
      "answer": 0,
      "explanation": "300−80−120＝100萬元；這是依題設計算的缺口，不是投保適合度判斷。"
    },
    "sources": [
      {
        "label": "金管會宣導｜檢視保障需求與保單條款",
        "url": "https://www.cip.gov.tw/zh-tw/news/data-list/EF33C0A18B5B5623/0747E487E929081EEAED55A4348E54EF-info.html",
        "kind": "official"
      },
      {
        "label": "金管會｜保險法與契約當事人、告知義務",
        "url": "https://law.fsc.gov.tw/LawContent.aspx?id=FL006746",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台灣生活情境；保險與房貸權利義務依實際契約及當時法規。所有金額、利率與家庭案例皆為假設，不是商品報價。"
  },
  {
    "id": "insurance-periods",
    "moduleId": "insurance",
    "title": "繳二十年，保障就一定只有二十年嗎？",
    "subtitle": "繳費期間、保障期間與續保",
    "minutes": 6,
    "tag": "用保障需求看保險",
    "objectives": [
      "分開看繳費年期與保障期間",
      "確認定期、終身、主約及附約的不同條件"
    ],
    "paragraphs": [
      "繳費期間回答要繳多久，保障期間回答在契約有效下保障多久，兩者可以不同。定期保障有約定期限；終身商品也須核對契約終期、給付與持續有效條件，不能只憑名稱推定每個附約都保一輩子。",
      "比較時對齊保額、保障事件、投保條件與保障期間，再讀續保、最高續保年齡和費率安排。保證續保也不等於保費永遠不變；具體變動仍看條款。",
      "下面的兩個方案是刻意設定的假想固定保費，只算名目繳費加總。保障時間不同，不能只靠保費總額判定哪個比較划算，也不把未理賠視為保險沒有提供過保障。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假想A提供20年保障，每年固定6,000元、繳20年，名目保費共120,000元。",
        "假想B按契約提供較長期間保障，每年固定35,000元、繳20年，名目保費共700,000元。",
        "兩者都繳20年，並不代表保障期間相同；還需要比較給付、條款與長期負擔，而非只比較120,000與700,000。"
      ],
      "conclusion": "先問保多久，再問繳多久，最後才比較同條件成本。"
    },
    "pitfalls": [
      "把主約終身保障套用到全部附約。",
      "把保證續保當成保費固定。"
    ],
    "glossary": [
      {
        "term": "繳費期間",
        "definition": "契約約定需要繳納保費的期間。"
      },
      {
        "term": "保障期間",
        "definition": "在契約有效及符合約定下，保險責任持續的期間。"
      }
    ],
    "takeaway": "先問保多久，再問繳多久，最後才比較同條件成本。",
    "exercise": "替兩份虛構保單做四格比較：繳費期間、保障期間、續保條件、費率是否可能改變。",
    "quiz": {
      "question": "一份商品繳費20年，僅由這句話能確認什麼？",
      "options": [
        "所有附約都終身保障",
        "約定繳費期間為20年，保障期間仍須另看",
        "20年後一定退還全部保費"
      ],
      "answer": 1,
      "explanation": "繳費期間不是保障期間或還本金額，不能互相代換。"
    },
    "sources": [
      {
        "label": "金管會｜保險法與契約當事人、告知義務",
        "url": "https://law.fsc.gov.tw/LawContent.aspx?id=FL006746",
        "kind": "official"
      },
      {
        "label": "金管會宣導｜檢視保障需求與保單條款",
        "url": "https://www.cip.gov.tw/zh-tw/news/data-list/EF33C0A18B5B5623/0747E487E929081EEAED55A4348E54EF-info.html",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台灣生活情境；保險與房貸權利義務依實際契約及當時法規。所有金額、利率與家庭案例皆為假設，不是商品報價。"
  },
  {
    "id": "insurance-terms",
    "moduleId": "insurance",
    "title": "保單上有寫疾病，為什麼未必都能理賠？",
    "subtitle": "告知、等待期間、除外與給付限制",
    "minutes": 6,
    "tag": "用保障需求看保險",
    "objectives": [
      "區分投保時告知和事故後理賠審查",
      "從條款辨認等待期間、自負額與上限"
    ],
    "paragraphs": [
      "依保險法第64條，訂約時要保人應對保險人的書面詢問據實說明。不要依口頭保證略過問項；有不清楚的問題應確認並留存往來。個別解除與理賠爭議仍須看法定要件及契約，不以本課直接判定。",
      "等待期間、除外責任、自負額、給付上限與續保條件分別回答不同問題。疾病、意外或不同保障可能使用不同規則，不能把某一個等待天數套用所有保單。",
      "以下所有條件由題目假設，不是任何真實保單。實支實付與定額給付的計算也不同，應依實際條款與理賠文件核對，不能以買了幾份就直接相乘。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假想契約明定某項疾病保障等待60天，自生效日起依契約方式計算；第45天發生該項疾病，尚未滿該等待期間。",
        "另設獨立理賠例：符合保障的費用30,000元、自負額5,000元、給付上限20,000元，約定先扣自負額再取上限。",
        "按這個假設條款，給付min(30,000−5,000,20,000)＝20,000元；並非所有契約都這樣計算。"
      ],
      "conclusion": "商品名稱只提示方向，條款才界定保障如何運作。"
    },
    "pitfalls": [
      "將某保單的等待期間當成法定統一期間。",
      "只看保額，漏掉自負額、除外或給付條件。"
    ],
    "glossary": [
      {
        "term": "等待期間",
        "definition": "契約對特定保障約定的起始等待時間，範圍和計算依條款。"
      },
      {
        "term": "自負額",
        "definition": "在約定條件下須自行承擔的費用部分，計算依契約。"
      }
    ],
    "takeaway": "商品名稱只提示方向，條款才界定保障如何運作。",
    "exercise": "只閱讀條款，標出等待、除外、給付限制與續保的位置；不需要填寫真實病史。",
    "quiz": {
      "question": "假設契約先從符合費用25,000元扣自負額3,000元，再受給付上限18,000元限制，給付是多少？",
      "options": [
        "25,000元",
        "22,000元",
        "18,000元"
      ],
      "answer": 2,
      "explanation": "min(25,000−3,000,18,000)＝18,000元；結論僅適用題設條款。"
    },
    "sources": [
      {
        "label": "金管會｜保險法與契約當事人、告知義務",
        "url": "https://law.fsc.gov.tw/LawContent.aspx?id=FL006746",
        "kind": "official"
      },
      {
        "label": "金管會宣導｜檢視保障需求與保單條款",
        "url": "https://www.cip.gov.tw/zh-tw/news/data-list/EF33C0A18B5B5623/0747E487E929081EEAED55A4348E54EF-info.html",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台灣生活情境；保險與房貸權利義務依實際契約及當時法規。所有金額、利率與家庭案例皆為假設，不是商品報價。"
  },
  {
    "id": "insurance-surrender",
    "moduleId": "insurance",
    "title": "解約拿回的錢，為什麼可能少於已繳保費？",
    "subtitle": "現金價值、解約金與保障變動",
    "minutes": 6,
    "tag": "用保障需求看保險",
    "objectives": [
      "分開看累積保費與可領解約金",
      "理解換約前需檢查新舊保障銜接"
    ],
    "paragraphs": [
      "保費包含取得保障及契約運作的成本，不能一律當成可隨時全領回的存款。可領取多少解約金，應依契約、年度、保單價值及相關扣款核對，不能只看已繳保費加總。",
      "利率變動型商品的宣告利率不等於固定或保證報酬，更不等於保單整體年化報酬。不同商品應分清保證與非保證項目；不要拿單一利率直接套在每一期保費上。",
      "考慮停止或調整舊契約時，還要查保障是否消失、附約如何處理、新契約是否重新核保或有等待期間，以及有無其他可選調整方式。本課只列查核事項，不建議某人直接解約或換約。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假設已繳保費共120,000元，本年度依條款可領解約金85,000元，另無借款及其他扣款。",
        "現金差額120,000−85,000＝35,000元；85,000元才是題設可領金額。",
        "差額不是單憑一個起訖數字就可年化的投資損失，期間內也可能已有保障；應再檢查契約與保障需求。"
      ],
      "conclusion": "解約比較要同時看現金與保障，不能只看已繳多少。"
    },
    "pitfalls": [
      "把宣告利率當成保證的保單報酬率。",
      "在新保障確認前，就只為退費數字停止原保障。"
    ],
    "glossary": [
      {
        "term": "解約金",
        "definition": "按契約與適用規定，在終止時可領取的金額，未必等於保費加總。"
      },
      {
        "term": "非保證項目",
        "definition": "未被契約保證固定金額或固定條件的項目，不能視為確定給付。"
      }
    ],
    "takeaway": "解約比較要同時看現金與保障，不能只看已繳多少。",
    "exercise": "列一份不含個資的換約查核清單：舊保障、新核保、等待期間與現金價值。",
    "quiz": {
      "question": "已繳150,000元，假設本年度可領解約金105,000元且無其他扣款，哪個解讀正確？",
      "options": [
        "現金差45,000元，但還要一起評估保障變動",
        "契約保證另外退45,000元",
        "只要宣告利率為正，就必定拿回全部保費"
      ],
      "answer": 0,
      "explanation": "150,000−105,000＝45,000元。這不表示另有補退保證，也未單獨衡量期間保障。"
    },
    "sources": [
      {
        "label": "金管會｜人身保險商品審查應注意事項",
        "url": "https://law.fsc.gov.tw/LawContent.aspx?id=FL040352&kw=110&media=print",
        "kind": "official"
      },
      {
        "label": "行政院消保會｜宣告利率與解約金資訊",
        "url": "https://cpc.ey.gov.tw/Page/8AC028685E311A7/79182d2e-d5e2-40c5-9a80-6e9aac905a9b",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台灣生活情境；保險與房貸權利義務依實際契約及當時法規。所有金額、利率與家庭案例皆為假設，不是商品報價。"
  }
];
